import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/apiClient";
import { PageHeader } from "../components/Layout";
import { toast } from "sonner";
import { PlusCircle, Trash2, Power, PowerOff, Copy, X, Info, Key, ExternalLink, BookOpen } from "lucide-react";

export default function ApiKeysPage() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [createdKey, setCreatedKey] = useState(null); // {name, api_key}
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDocs, setShowDocs] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/api-keys");
      setKeys(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    const name = (newName || "").trim();
    if (name.length < 2) { toast.error("İsim en az 2 karakter olmalı"); return; }
    setSaving(true);
    try {
      const r = await api.post("/api-keys", { name });
      setCreatedKey(r.data);
      setShowNew(false);
      setNewName("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const toggle = async (k) => {
    try {
      await api.patch(`/api-keys/${k.id}/toggle`);
      toast.success(k.is_active ? "Anahtar devre dışı bırakıldı" : "Anahtar aktif edildi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api-keys/${deleteTarget.id}`);
      toast.success("Anahtar silindi");
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Panoya kopyalandı");
    } catch {
      toast.error("Kopyalama başarısız");
    }
  };

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  return (
    <div>
      <PageHeader
        title="API Anahtarları"
        subtitle="Dış sistemlerin siparişlerinizi okuyabilmesi için güvenli API anahtarları oluşturun."
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => setShowDocs(true)} data-testid="api-docs-btn"
              className="inline-flex items-center gap-2 px-4 py-2 border-2 border-slate-800 hover:bg-slate-100 text-slate-900 font-bold text-sm uppercase">
              <BookOpen size={16}/> API Dokümanı
            </button>
            <button onClick={() => setShowNew(true)} data-testid="api-key-new-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white font-bold text-sm uppercase">
              <PlusCircle size={16}/> Yeni Anahtar
            </button>
          </div>
        }
      />

      <div className="mb-4 p-3 border-l-4 border-blue-500 bg-blue-50 text-blue-900 text-sm flex items-start gap-2">
        <Info size={18} className="mt-0.5 shrink-0"/>
        <div>
          Her anahtar <b>yalnızca bir kez</b> gösterilir. Dış sistem bu anahtarı <code className="font-mono px-1 bg-white">X-API-Key</code> başlığıyla göndermelidir.
          Anahtarı kaybederseniz yeni bir tane oluşturmanız gerekir.
        </div>
      </div>

      {loading && <div className="text-slate-500">Yükleniyor...</div>}

      {!loading && (
        <section className="card-industrial">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
            <Key size={16} className="text-slate-600"/>
            <span className="font-display font-black text-slate-900 text-base">Mevcut Anahtarlar</span>
            <span className="text-xs text-slate-500">({keys.length})</span>
          </div>
          {keys.length === 0 ? (
            <div className="px-4 py-8 text-sm text-slate-400 italic text-center">
              Henüz API anahtarı oluşturulmamış. Dış sistem entegrasyonu için yukarıdan oluşturun.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-widest text-slate-600">
                  <th className="text-left px-4 py-2 font-bold">İsim</th>
                  <th className="text-left px-4 py-2 font-bold">Önek</th>
                  <th className="text-left px-4 py-2 font-bold">Durum</th>
                  <th className="text-left px-4 py-2 font-bold">Kullanım</th>
                  <th className="text-left px-4 py-2 font-bold">Son Kullanım</th>
                  <th className="text-left px-4 py-2 font-bold">Oluşturulma</th>
                  <th className="text-right px-4 py-2 font-bold">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k, i) => (
                  <tr key={k.id} data-testid={`api-key-row-${k.id}`}
                    className={`border-b border-slate-100 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                    <td className="px-4 py-2 font-bold text-slate-900">{k.name}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-700">{k.prefix}•••••</td>
                    <td className="px-4 py-2">
                      {k.is_active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 border border-green-400 text-xs font-bold uppercase">
                          <Power size={11}/> Aktif
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-200 text-slate-600 border border-slate-400 text-xs font-bold uppercase">
                          <PowerOff size={11}/> Pasif
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 font-mono tabular-nums text-slate-700">{k.usage_count || 0}</td>
                    <td className="px-4 py-2 text-xs text-slate-600">
                      {k.last_used_at ? new Date(k.last_used_at).toLocaleString("tr-TR") : <span className="text-slate-400 italic">Hiç</span>}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600">
                      {k.created_at ? new Date(k.created_at).toLocaleString("tr-TR") : "-"}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => toggle(k)} data-testid={`api-key-toggle-${k.id}`}
                        className={`inline-flex items-center gap-1 text-xs font-bold mr-3 ${k.is_active ? "text-amber-700 hover:text-amber-900" : "text-green-700 hover:text-green-900"}`}>
                        {k.is_active ? <><PowerOff size={13}/> Devre Dışı</> : <><Power size={13}/> Aktifleştir</>}
                      </button>
                      <button onClick={() => setDeleteTarget(k)} data-testid={`api-key-delete-${k.id}`}
                        className="inline-flex items-center gap-1 text-red-700 hover:text-red-900 text-xs font-bold">
                        <Trash2 size={13}/> Sil
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {/* New key dialog */}
      {showNew && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={() => setShowNew(false)}>
          <div className="bg-white max-w-md w-full p-5 shadow-xl border-2 border-slate-800" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-black text-lg">Yeni API Anahtarı</h3>
              <button onClick={() => setShowNew(false)} className="p-1 hover:bg-slate-100"><X size={18}/></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] uppercase tracking-widest font-bold text-slate-600">
                  Anahtar İsmi <span className="text-slate-400 font-normal normal-case">(entegrasyonu tanımlayan açıklama)</span>
                </label>
                <input value={newName} onChange={(e) => setNewName(e.target.value)}
                  placeholder="Örn: Muhasebe Sistemi, ERP Entegrasyonu"
                  data-testid="api-key-form-name" autoFocus
                  className="w-full mt-1 px-3 py-2 border-2 border-slate-800 focus:border-blue-700 focus:outline-none font-bold"
                />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowNew(false)} className="px-4 py-2 border border-slate-300 hover:bg-slate-100 text-sm font-bold uppercase">
                Vazgeç
              </button>
              <button onClick={create} disabled={saving} data-testid="api-key-form-save"
                className="inline-flex items-center gap-2 px-5 py-2 bg-blue-700 hover:bg-blue-800 text-white font-bold text-sm uppercase disabled:opacity-50">
                {saving ? "Oluşturuluyor..." : "Oluştur"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Created key — shown ONCE */}
      {createdKey && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4">
          <div className="bg-white max-w-xl w-full p-5 shadow-xl border-4 border-amber-500" data-testid="api-key-created-dialog">
            <h3 className="font-display font-black text-lg text-amber-900 mb-2 flex items-center gap-2">
              <Key size={20}/> Anahtar Oluşturuldu — Bir Kez Gösterilir!
            </h3>
            <p className="text-sm text-slate-700 mb-3">
              <b>{createdKey.name}</b> için anahtarı aşağıdan kopyalayın. Bu pencereyi kapattıktan sonra tam anahtar bir daha gösterilmeyecek.
            </p>
            <div className="bg-slate-900 text-green-300 font-mono text-xs p-3 break-all mb-3 border-2 border-slate-700" data-testid="api-key-created-value">
              {createdKey.api_key}
            </div>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => copy(createdKey.api_key)}
                className="inline-flex items-center gap-2 px-4 py-2 border-2 border-slate-800 hover:bg-slate-100 text-sm font-bold uppercase">
                <Copy size={14}/> Kopyala
              </button>
              <button onClick={() => setCreatedKey(null)} data-testid="api-key-created-close"
                className="px-5 py-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold uppercase">
                Kaydettim, Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={() => setDeleteTarget(null)}>
          <div className="bg-white max-w-md w-full p-5 shadow-xl border-2 border-red-600" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-black text-lg text-red-800 mb-2">Anahtarı Sil</h3>
            <p className="text-sm text-slate-700 mb-4">
              <b>{deleteTarget.name}</b> anahtarı silinecek. Bu anahtarı kullanan dış sistemler bağlantıyı kaybeder. Devam edilsin mi?
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 border border-slate-300 hover:bg-slate-100 text-sm font-bold uppercase">
                Vazgeç
              </button>
              <button onClick={doDelete} data-testid="api-key-delete-confirm"
                className="inline-flex items-center gap-2 px-5 py-2 bg-red-700 hover:bg-red-800 text-white font-bold text-sm uppercase">
                <Trash2 size={16}/> Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick docs */}
      {showDocs && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={() => setShowDocs(false)}>
          <div className="bg-white max-w-3xl w-full max-h-[85vh] overflow-y-auto p-5 shadow-xl border-2 border-slate-800" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-black text-xl">Hızlı API Rehberi</h3>
              <button onClick={() => setShowDocs(false)} className="p-1 hover:bg-slate-100"><X size={20}/></button>
            </div>

            <div className="space-y-4 text-sm text-slate-800">
              <section>
                <h4 className="font-display font-black text-base mb-1">Kimlik Doğrulama</h4>
                <p>Her isteğe <code className="font-mono bg-slate-100 px-1">X-API-Key</code> başlığı ekleyin:</p>
                <pre className="bg-slate-900 text-green-300 font-mono text-xs p-3 mt-2 overflow-x-auto border border-slate-700">
X-API-Key: mb_xxxxxxxxxxxxxxxxxxxxx
                </pre>
              </section>

              <section>
                <h4 className="font-display font-black text-base mb-1">Base URL</h4>
                <pre className="bg-slate-900 text-green-300 font-mono text-xs p-3 overflow-x-auto border border-slate-700">
{apiBase || "https://alan-adiniz.com"}/api/external
                </pre>
              </section>

              <section>
                <h4 className="font-display font-black text-base mb-1">Endpoint'ler</h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li><code className="font-mono bg-slate-100 px-1">GET /ping</code> — Bağlantı testi</li>
                  <li><code className="font-mono bg-slate-100 px-1">GET /orders</code> — Siparişleri listele (sayfalama + filtre)</li>
                  <li><code className="font-mono bg-slate-100 px-1">GET /orders/by-number/{"{order_number}"}</code> — Sipariş numarasıyla getir</li>
                  <li><code className="font-mono bg-slate-100 px-1">GET /orders/{"{order_id}"}</code> — ID ile detaylı getir</li>
                  <li><code className="font-mono bg-slate-100 px-1">GET /pieces/{"{barcode}"}</code> — Barkod bilgisi</li>
                  <li><code className="font-mono bg-slate-100 px-1">GET /orders.xlsx</code> — <b>Excel (.xlsx) indir</b> — URL'den indirme destekli dış programlar için</li>
                </ul>
              </section>

              <section>
                <h4 className="font-display font-black text-base mb-1">Dış Programa Ekleyeceğiniz Excel URL</h4>
                <p className="text-xs mb-2">Diğer sipariş programınız bir URL'den <code className="font-mono bg-slate-100 px-1">.xlsx</code> indiriyorsa aşağıdaki örneği kullanın (anahtarınızı URL'ye ekleyin):</p>
                <pre className="bg-slate-900 text-green-300 font-mono text-xs p-3 overflow-x-auto border border-slate-700">
{apiBase || "https://alan-adiniz.com"}/api/external/orders.xlsx?api_key=mb_xxxxxxx
                </pre>
                <p className="text-xs text-amber-700 mt-2 italic">
                  ⚠ Query param ile anahtar paylaşıldığında logs/history'de görünebilir — mümkünse <code className="font-mono bg-slate-100 px-1">X-API-Key</code> header'ı tercih edin.
                </p>
              </section>

              <section>
                <h4 className="font-display font-black text-base mb-1">Örnek — Siparişleri çek</h4>
                <pre className="bg-slate-900 text-green-300 font-mono text-xs p-3 overflow-x-auto border border-slate-700">
curl -H "X-API-Key: mb_xxxxxxx" \
  "{apiBase || "https://alan-adiniz.com"}/api/external/orders?limit=10&status=READY"
                </pre>
              </section>

              <section>
                <h4 className="font-display font-black text-base mb-1">Sipariş Durum Değerleri (overall_status)</h4>
                <ul className="list-disc pl-5">
                  <li><code className="font-mono bg-slate-100 px-1">ORDER_RECEIVED</code> — Sipariş alındı (parça yok)</li>
                  <li><code className="font-mono bg-slate-100 px-1">PRODUCTION_PLANNED</code> — Parçalar oluşturuldu, üretim bekliyor</li>
                  <li><code className="font-mono bg-slate-100 px-1">IN_PRODUCTION</code> — Üretim devam ediyor</li>
                  <li><code className="font-mono bg-slate-100 px-1">READY</code> — Tüm parçalar hazır</li>
                  <li><code className="font-mono bg-slate-100 px-1">SHIPPED</code> — Tamamı sevk edildi</li>
                </ul>
              </section>

              <p className="text-xs text-slate-500 italic">
                Tam dokümantasyon için proje kök dizinindeki <code className="font-mono bg-slate-100 px-1">API_DOCUMENTATION.md</code> dosyasına bakın.
              </p>
            </div>

            <div className="mt-4 flex justify-end">
              <button onClick={() => setShowDocs(false)} className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold uppercase">
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
